import { useSearchParams } from 'next/navigation';
import { extractAndRemoveHash } from '@/utils/url-helper';
import { useLocalization } from '../providers/localizationProvider';
import {
  getLocaleLink,
  isPathExternalLink,
  removeOverviewPagePath,
} from '../utils/localization-helpers/localization';

export const useLocaleLink = () => {
  const context = useLocalization();

  const currentSearchParams = useSearchParams();

  // Returns localized path eg: /en-uk/checkout
  const localizedLink = (
    path: string,
    maintainParams: boolean = false,
    language: string = context.language,
    country: string = context.country,
  ): string => {
    if (isPathExternalLink(path)) {
      return path;
    }

    // Extract and remove the hash fragment from the path (if any).
    const { pathWithoutHash, hashFragment } = extractAndRemoveHash(path);

    let searchParams = new URLSearchParams();

    // Add current search params if maintainParams is true
    if (maintainParams) {
      searchParams = new URLSearchParams(currentSearchParams);
    }

    // Extract existing search params from the path, if any
    const [basePath, pathSearch] = pathWithoutHash.split('?');

    // Add path params from the path
    if (pathSearch) {
      const pathParams = new URLSearchParams(pathSearch);
      pathParams.forEach((value, key) => {
        searchParams.append(key, value);
      });
    }

    // Construct the search part of the URL
    const searchPart = searchParams.toString() ? `?${searchParams.toString()}` : '';

    const parsedPath = removeOverviewPagePath(basePath);

    let localeLink = getLocaleLink(`${parsedPath}${searchPart}`, language, country);

    if (hashFragment) {
      localeLink += `#${hashFragment}`;
    }
    return localeLink;
  };

  // Returns full localized url with current host eg: http://localhost:3000/en-uk/checkout
  function localizedUrl(path: string, maintainParams: boolean = false): string {
    return `${context.baseUrl}${localizedLink(path, maintainParams)}`;
  }

  return { localizedLink, localizedUrl };
};
